import './CommentItem.scss';

import { useUserContext } from '@context/UserContext';
import LockIcon from '@mui/icons-material/Lock';
import { Alert, Avatar, Box, Button, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';

import { CommentItemProps } from './CommentsDrawer.types';

const CommentItem = (props: CommentItemProps) => {
  const { user, timestamp, commentDesc, index, metadata, onDelete, commentId, isEditingDisabled } = props;
  const { username } = useUserContext();
  // Note: this may seem backwards, but is zero-indexed
  const zebraStripeClass = index % 2 === 0 ? 'odd' : 'even';

  /**
   * Renders `(delete)` action when user owns the CommentItem
   */
  const renderDeleteAction = () => {
    if (user !== username) return null;
    if (metadata.isPendingDelete) {
      return (
        <Button
          disabled={isEditingDisabled}
          onClick={() => onDelete(commentId)}
          variant='text'
          className='comment-button-delete'
          endIcon={isEditingDisabled && <LockIcon />}
        >
          undo delete
        </Button>
      );
    }

    return (
      <Button
        disabled={isEditingDisabled}
        onClick={() => onDelete(commentId)}
        variant='text'
        className='comment-button-delete'
        endIcon={isEditingDisabled && <LockIcon />}
      >
        delete comment
      </Button>
    );
  };

  /**
   * Displays user and timestamp
   */
  const renderPrimaryText = () => {
    const localTimestamp = new Date(timestamp).toLocaleString();
    return (
      <Box className='comment-primary-text'>
        <Box className='comment-user'>{user}</Box>
        <Box className='comment-timestamp'>{localTimestamp}</Box>
      </Box>
    );
  };

  /**
   * Displays the comment description and the unsaved alert if the comment is pending save
   */
  const renderSecondaryText = () => {
    return (
      <Stack className='comment-secondary-text' spacing={0} direction='column'>
        {(metadata.isPendingSave || metadata.isPendingDelete) && (
          <Alert variant='standard' severity='info' className='comment-item-unsaved-alert'>
            <Typography variant='body2' component='span'>
              {/* Literal templates used to preserve spacing */}
              {'Unsaved Changes: '}
              <Typography variant='inherit' component='strong' className='comment-strong-text'>
                Save Page
              </Typography>
              {' to Finalize'}
            </Typography>
          </Alert>
        )}
        <Typography className={metadata.isPendingDelete ? 'comment-pending-delete' : ''}>{commentDesc}</Typography>
        {renderDeleteAction()}
      </Stack>
    );
  };

  return (
    <ListItem className={`comment-item ${zebraStripeClass}`}>
      <ListItemAvatar>
        <Avatar>{user.charAt(0).toUpperCase()}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={renderPrimaryText()}
        secondary={renderSecondaryText()}
        slotProps={{ secondary: { component: 'div' } }}
      />
    </ListItem>
  );
};

export default CommentItem;
